import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FolderOpen, Search, Trash2, Copy, Pencil, Tag, X, Loader2, Reply, Share2, Volume2, Square } from "lucide-react";
import { toast } from "sonner";

const when = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

export const LetterFolder = ({ languages = [], refreshKey = 0, onOpen, onDeleted, activeId }) => {
  const [q, setQ] = useState("");
  const [label, setLabel] = useState("");
  const [data, setData] = useState({ letters: [], labels: [] });
  const [loading, setLoading] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [labelling, setLabelling] = useState(null);
  const [draftLabel, setDraftLabel] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/letters", { params: { q, label } })
        .then(({ data: d }) => !cancelled && setData(d))
        .catch((e) => console.error("could not load the letter folder", e))
        .finally(() => !cancelled && setLoading(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, label, refreshKey]);

  const reload = () =>
    api.get("/letters", { params: { q, label } }).then(({ data: d }) => setData(d));

  const langName = (code) => languages.find((l) => l.code === code)?.native || code;

  const full = async (id) => (await api.get(`/letters/${id}`)).data;

  const open = async (letter) => {
    try {
      onOpen(await full(letter.letter_id));
      toast.success(`“${letter.title}” is back in the translator`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not open that letter");
    }
  };

  const copy = async (letter) => {
    try {
      const doc = await full(letter.letter_id);
      const parts = [doc.translation];
      (doc.replies || []).forEach((r) => parts.push(`--- Reply ---\n${r.translation}`));
      await navigator.clipboard.writeText(parts.join("\n\n"));
      toast.success("Letter and replies copied");
    } catch {
      toast.error("Your browser blocked the copy");
    }
  };

  const rename = async (letter) => {
    const title = draftTitle.trim();
    if (!title) return;
    try {
      await api.patch(`/letters/${letter.letter_id}`, { title });
      setRenaming(null);
      await reload();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not rename that letter");
    }
  };

  const addLabel = async (letter) => {
    const name = draftLabel.trim();
    if (!name) return;
    try {
      await api.patch(`/letters/${letter.letter_id}`, { labels: [...(letter.labels || []), name] });
      setLabelling(null);
      setDraftLabel("");
      await reload();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not add that label");
    }
  };

  const removeLabel = async (letter, name) => {
    await api.patch(`/letters/${letter.letter_id}`, {
      labels: (letter.labels || []).filter((l) => l !== name),
    });
    await reload();
  };

  const [speaking, setSpeaking] = useState(null);
  const audioRef = useRef(null);
  const stopRef = useRef(false);

  const stopSpeech = () => {
    stopRef.current = true;
    audioRef.current?.pause();
    audioRef.current = null;
    setSpeaking(null);
  };

  useEffect(() => () => stopSpeech(), []);

  const playBlob = (blob) =>
    new Promise((resolve) => {
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => resolve(true);
      audio.onerror = () => resolve(false);
      audio.play().catch(() => resolve(false));
    });

  const listen = async (letter) => {
    if (speaking === letter.letter_id) {
      stopSpeech();
      return;
    }
    stopSpeech();
    stopRef.current = false;
    setSpeaking(letter.letter_id);
    try {
      const blocks = [["letter", 0]];
      for (let i = 0; i < letter.reply_count; i += 1) blocks.push(["reply", i]);
      for (const [which, index] of blocks) {
        let part = 0;
        let total = 1;
        while (part < total) {
          if (stopRef.current) return;
          const res = await api.get(`/letters/${letter.letter_id}/speech`, {
            params: { which, index, part },
            responseType: "blob",
          });
          total = Number(res.headers["x-parts"] || 1);
          if (stopRef.current) return;
          const finished = await playBlob(res.data);
          if (!finished || stopRef.current) return;
          part += 1;
        }
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not read that letter out");
    } finally {
      if (!stopRef.current) setSpeaking(null);
    }
  };

  const share = async (letter) => {    try {
      const { data } = await api.post(`/letters/${letter.letter_id}/share`);
      const link = `${window.location.origin}/lt/${data.share_id}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Private link copied — anyone with it can read the letter and your reply");
      } catch {
        toast.message(link);
      }
      await reload();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not make a link for that letter");
    }
  };

  const remove = async (letter) => {
    try {
      await api.delete(`/letters/${letter.letter_id}`);
      if (letter.letter_id === activeId) onDeleted?.(letter.letter_id);
      await reload();
      toast.success("Removed from your folder");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not delete that letter");
    }
  };

  return (
    <div className="mt-8 border-t border-white/10 pt-7" data-testid="letter-folder">
      <p className="inline-flex items-center gap-2 font-display text-lg font-bold tracking-tight">
        <FolderOpen className="h-4 w-4 text-brand" /> My Letters
      </p>
      <p className="mt-1 text-sm text-ink-soft">
        Every letter you keep — with the replies drafted for it — stays here. Search any word in it.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="relative flex-1 min-w-[14rem]">
          <Search className="pointer-events-none absolute start-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-soft" />
          <input
            data-testid="letter-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search letters, replies and notes…"
            className="min-h-[44px] w-full rounded-full border border-white/10 bg-page ps-10 pe-4 text-sm outline-none placeholder:text-ink-soft focus:border-brand/50"
          />
        </span>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-ink-soft" />}
      </div>

      {data.labels.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="letter-label-filters">
          <button
            data-testid="letter-label-all"
            onClick={() => setLabel("")}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              label === "" ? "border-brand bg-brand/15 text-brand" : "border-white/10 text-ink-soft hover:text-ink"
            }`}
          >
            All
          </button>
          {data.labels.map((l) => (
            <button
              key={l}
              data-testid={`letter-label-${l}`}
              onClick={() => setLabel(label === l ? "" : l)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                label === l ? "border-brand bg-brand/15 text-brand" : "border-white/10 text-ink-soft hover:text-ink"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      )}

      {data.letters.length === 0 && !loading && (
        <p className="mt-4 text-sm text-ink-soft" data-testid="letter-folder-empty">
          {q || label
            ? "Nothing matches that yet."
            : "Nothing kept yet — translate a letter and tap “Save to my letters”."}
        </p>
      )}

      <div className="mt-4 space-y-3">
        {data.letters.map((letter) => (
          <div
            key={letter.letter_id}
            data-testid={`letter-card-${letter.letter_id}`}
            className={`rounded-2xl border bg-page p-4 ${
              activeId === letter.letter_id ? "border-brand/60" : "border-white/10"
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                {renaming === letter.letter_id ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <input
                      data-testid="letter-rename-input"
                      autoFocus
                      value={draftTitle}
                      maxLength={120}
                      onChange={(e) => setDraftTitle(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && rename(letter)}
                      className="min-h-[36px] rounded-full border border-brand/40 bg-panel px-4 text-sm outline-none"
                    />
                    <Button
                      data-testid="letter-rename-save"
                      size="sm"
                      variant="outline"
                      onClick={() => rename(letter)}
                      className="min-h-[36px] rounded-full px-4 text-xs"
                    >
                      Save
                    </Button>
                  </span>
                ) : (
                  <p
                    dir="auto"
                    data-testid={`letter-title-${letter.letter_id}`}
                    className="font-display text-sm font-bold tracking-tight"
                  >
                    {letter.title}
                  </p>
                )}
                <p className="mt-0.5 text-xs text-ink-soft">
                  {when(letter.created_at)} · {langName(letter.target)}
                  {letter.reply_count > 0 && (
                    <span className="ms-2 inline-flex items-center gap-1 text-brand">
                      <Reply className="h-3 w-3" /> {letter.reply_count}{" "}
                      {letter.reply_count === 1 ? "reply" : "replies"}
                    </span>
                  )}
                </p>
              </div>
              <span className="flex shrink-0 flex-wrap items-center gap-1.5">
                <Button
                  data-testid={`letter-open-${letter.letter_id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => open(letter)}
                  className="min-h-[36px] rounded-full px-4 text-xs"
                >
                  Open
                </Button>
                <button
                  data-testid={`letter-copy-${letter.letter_id}`}
                  onClick={() => copy(letter)}
                  aria-label="Copy this letter"
                  className="rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`letter-listen-${letter.letter_id}`}
                  onClick={() => listen(letter)}
                  aria-label="Hear this letter and its replies"
                  title={speaking === letter.letter_id ? "Stop" : "Hear the letter and replies"}
                  className={`rounded-full border p-1.5 transition-colors hover:border-brand/50 hover:text-brand ${
                    speaking === letter.letter_id ? "border-brand bg-brand/15 text-brand" : "border-white/10 text-ink-soft"
                  }`}
                >
                  {speaking === letter.letter_id ? (
                    <Square className="h-3.5 w-3.5" />
                  ) : (
                    <Volume2 className="h-3.5 w-3.5" />
                  )}
                </button>
                <button
                  data-testid={`letter-share-${letter.letter_id}`}
                  onClick={() => share(letter)}
                  aria-label="Share this letter with family"
                  className={`rounded-full border p-1.5 transition-colors hover:border-brand/50 hover:text-brand ${
                    letter.share_id ? "border-brand/40 text-brand" : "border-white/10 text-ink-soft"
                  }`}
                >
                  <Share2 className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`letter-rename-${letter.letter_id}`}
                  onClick={() => {
                    setRenaming(letter.letter_id);
                    setDraftTitle(letter.title);
                  }}
                  aria-label="Rename this letter"
                  className="rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`letter-label-add-${letter.letter_id}`}
                  onClick={() => {
                    setLabelling(labelling === letter.letter_id ? null : letter.letter_id);
                    setDraftLabel("");
                  }}
                  aria-label="Label this letter"
                  className="rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                >
                  <Tag className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`letter-delete-${letter.letter_id}`}
                  onClick={() => remove(letter)}
                  aria-label="Delete this letter"
                  className="rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-destructive/60 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            </div>

            {(letter.labels || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`letter-labels-${letter.letter_id}`}>
                {letter.labels.map((l) => (
                  <span
                    key={l}
                    className="inline-flex items-center gap-1 rounded-full border border-brand/30 bg-brand/10 px-2.5 py-1 text-[11px] text-brand"
                  >
                    {l}
                    <button
                      data-testid={`letter-label-remove-${letter.letter_id}-${l}`}
                      onClick={() => removeLabel(letter, l)}
                      aria-label={`Remove label ${l}`}
                      className="opacity-60 hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {labelling === letter.letter_id && (
              <div className="mt-2 flex flex-wrap gap-2">
                <input
                  data-testid="letter-label-input"
                  autoFocus
                  value={draftLabel}
                  maxLength={24}
                  onChange={(e) => setDraftLabel(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addLabel(letter)}
                  placeholder="e.g. Landlord"
                  className="min-h-[36px] rounded-full border border-white/10 bg-panel px-4 text-sm outline-none placeholder:text-ink-soft focus:border-brand/50"
                />
                <Button
                  data-testid="letter-label-save"
                  size="sm"
                  variant="outline"
                  onClick={() => addLabel(letter)}
                  className="min-h-[36px] rounded-full px-4 text-xs"
                >
                  Add label
                </Button>
              </div>
            )}

            <p dir="auto" className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-ink-soft">
              {letter.preview}
            </p>
            {letter.last_reply && (
              <p
                dir="auto"
                data-testid={`letter-last-reply-${letter.letter_id}`}
                className="mt-2 line-clamp-2 whitespace-pre-wrap border-s-2 border-brand/40 ps-3 text-sm text-ink-soft"
              >
                {letter.last_reply}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
